import { BrowserModule } from '@angular/platform-browser';
import {
  NgModule,
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';

// Core
import { HttpErrorInterceptor } from './core';

// App Components
import { NavbarComponent, SidebarComponent } from './layouts/index';
import { VentaModule, DevolucionModule } from './modules/index';
import { SharedModule } from './shared/shared.module';

// Perfect Scrollbar
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { TokenInterceptor } from './core/http-helpers/token.interceptor';
import { GestionarModule } from './modules/gestionar';
import { LoginComponent } from './core/auth/login/login.component';
import { HomeComponent } from './home/home.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppConfiguration } from './app.configuration';
import { LocalizadorModule } from './modules/localizador/localizador.module';
import { GestionarVentaModule } from './modules/gestionar-venta/gestionar-venta.module';
import { AuditorioModule } from './modules/auditorio/auditorio.module';
import { SuccessToast } from './core/toast/success.toast';
import { ErrorToast } from './core/toast/error.toast';
import { InfoToast } from './core/toast/info.toast';
import { AgendaCalendarioComponent } from './modules/agenda/components/agenda-calendario/agenda-calendario.component';
import { ModalDatosAdicionalesEntradaComponent } from './modules/venta/components/modal-datos-adicionales-entrada/modal-datos-adicionales-entrada.component';
import { MatRadioModule } from '@angular/material/radio';
import { ConfirmarReimpresionDiaDistintoComponent } from './shared/components/confirmar-reimpresion-dia-distinto/confirmar-reimpresion-dia-distinto.component';
import { ImpresionLocalizadorModalComponent } from './shared/components/impresion-localizador-modal/impresion-localizador-modal.component';
import { AgendaLoginComponent } from './modules/agenda/components/agenda-login/agenda-login.component';
import { AutofocusFixModule } from 'ngx-autofocus-fix';
import { ConfirmarVentaReservaPagadaComponent } from './shared/components/confirmar-venta-reserva-pagada/confirmar-venta-reserva-pagada.component';
import { CambiarPasswordComponent } from './core/auth/cambiar-password.component';
import { ClienteAsociadoComponent } from './modules/Asociado/components/cliente-asociado/cliente-asociado.component';
import { CodigoAuthComponent } from './core/auth/codigo-auth/codigo-auth.component';
import { CommonModule } from '@angular/common';
import { TiendaModule } from './modules/tienda/tienda.module';
import { RequestMicroserviceInterceptor } from './core/http-helpers/microserviceInterceptor';
import { RestauracionModule } from './modules/restauracion/restauracion.module';
import { GestionarPausadasModule } from './modules/gestionar-pausadas/gestionar-pausadas.module';
import { AmigosThyssenModule } from './modules/amigos-thyssen/amigos-thyssen.module';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from 'src/environments/environment';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1; // Remove this line to use Angular Universal

export function loggerCallback(logLevel: LogLevel, message: string) {
  if (!environment['HAS_LOGIN_MICROSOFT']) {
    return; // Skip logging if Microsoft login is not enabled
  }
  // console.log(message);
  return;
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment['MSAL_CLIENT_ID'] || '',
      // clientId: '3fba556e-5d4a-48e3-8e1a-fd57c12cb82e', // PPE testing environment
      authority: environment['MSAL_AUTHORITY'] || '',
      redirectUri: environment['MSAL_REDIRECT_URI'] || '', // This should match the redirect URI configured in Azure AD
      postLogoutRedirectUri:
        environment['MSAL_POST_LOGOUT_REDIRECT_URI'] || '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
    },
    system: {
      allowRedirectInIframe: true,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    ...(environment['MSAL_SCOPES'] || ['user.read']),
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment['MSAL_SCOPES'] || ['user.read'],
    },
    loginFailedRoute: environment['MSAL_LOGIN_FAILED_ROUTE'] || '/',
  };
}
@NgModule({
  declarations: [
    AppComponent,
    NavbarComponent,
    SidebarComponent,
    LoginComponent,
    HomeComponent,
    SuccessToast,
    ErrorToast,
    InfoToast,
    AgendaCalendarioComponent,
    ModalDatosAdicionalesEntradaComponent,
    ConfirmarReimpresionDiaDistintoComponent,
    ImpresionLocalizadorModalComponent,
    AgendaLoginComponent,
    ConfirmarVentaReservaPagadaComponent,
    CambiarPasswordComponent,
    ClienteAsociadoComponent,
    CodigoAuthComponent,

  ],
  imports: [
    CommonModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    VentaModule,
    TiendaModule,
    RestauracionModule,
    DevolucionModule,
    GestionarModule,
    LocalizadorModule,
    GestionarPausadasModule,
    PerfectScrollbarModule,
    HttpClientModule,
    SharedModule,
    AuditorioModule,
    GestionarVentaModule,
    AmigosThyssenModule,
    BrowserAnimationsModule,
    MatRadioModule,
    MsalModule,
    AutofocusFixModule.forRoot(),
    ToastrModule.forRoot({
      timeOut: 1000,
      positionClass: 'toast-top-right',
    }),
  ],
  providers: [
    AppConfiguration,
    {
      provide: APP_INITIALIZER,
      useFactory: (config: AppConfiguration) => () => config.load(),
      deps: [AppConfiguration],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestMicroserviceInterceptor,
      multi: true,
    },
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
